import {
  FALLBACK_COLUMN_WIDTH,
  type TimelineLegendItem,
} from "./timelineUtils";

export const MILESTONES_TIMELINE_VISIBLE_COLUMNS = [
  "trial",
  "event_type",
  "event_name",
  "start_date",
  "end_date",
] as const;

export const MILESTONES_TIMELINE_COLUMN_LABELS: Record<string, string> = {
  trial: "Trial",
  event_type: "Event Type",
  event_name: "Event Name",
  start_date: "Start",
  end_date: "End",
};

const MILESTONES_TIMELINE_COLUMN_WIDTHS: Record<string, number> = {
  trial: 160,
  event_type: 170,
  event_name: 170,
  start_date: 120,
  end_date: 120,
};

const milestonesLegendMapping = {
  "Actual Date": "Actual Date",
  "Planned Date": "Planned Date",
  "Forecast Date": "Forecast Date",
} as const;

type MilestonesLegendKey = keyof typeof milestonesLegendMapping;

const milestonesLegendBorderColor: Record<MilestonesLegendKey, string> = {
  "Actual Date": "#0C98D0",
  "Planned Date": "#BCA048",
  "Forecast Date": "#C275A1",
};

const milestonesLegendFillColor: Record<MilestonesLegendKey, string> = {
  "Actual Date": "#D2EFF9",
  "Planned Date": "#FDE6A0",
  "Forecast Date": "#EBD1E0",
};

export function getMilestonesTimelineTableColumnWidth(column: string): number {
  return MILESTONES_TIMELINE_COLUMN_WIDTHS[column] ?? FALLBACK_COLUMN_WIDTH;
}

export function getMilestonesTimelineLegendFillColor(value: string): string {
  return milestonesLegendFillColor[value as MilestonesLegendKey] ?? "";
}

export function getMilestonesTimelineLegendBorderColor(value: string): string {
  return milestonesLegendBorderColor[value as MilestonesLegendKey] ?? "";
}

export function getMilestonesTimelineLegendBorderColorFromFill(
  fillColor: string,
): string {
  const entry = (
    Object.keys(milestonesLegendFillColor) as MilestonesLegendKey[]
  ).find(
    (key) =>
      milestonesLegendFillColor[key].toLowerCase() === fillColor.toLowerCase(),
  );

  return entry ? milestonesLegendBorderColor[entry] : "";
}

export function getMilestonesTimelineLegendItems(
  keys?: string[],
): TimelineLegendItem[] {
  const legendKeys = keys?.length
    ? keys.filter(
        (key): key is MilestonesLegendKey => key in milestonesLegendMapping,
      )
    : (Object.keys(milestonesLegendMapping) as MilestonesLegendKey[]);

  return legendKeys
    .map((key) => ({
      key,
      label: milestonesLegendMapping[key],
      borderColor: milestonesLegendBorderColor[key],
      fillColor: milestonesLegendFillColor[key],
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export const milestonesTimelineUtils = {
  defaultVisibleColumns: MILESTONES_TIMELINE_VISIBLE_COLUMNS,
  columnLabels: MILESTONES_TIMELINE_COLUMN_LABELS,
  getTableColumnWidth: getMilestonesTimelineTableColumnWidth,
  getLegendFillColor: getMilestonesTimelineLegendFillColor,
  getLegendBorderColor: getMilestonesTimelineLegendBorderColor,
  getLegendBorderColorFromFill: getMilestonesTimelineLegendBorderColorFromFill,
  getAllLegendItems: getMilestonesTimelineLegendItems,
};
